import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useProjectPanelStore, useFilteredAssets } from '../../store/projectPanelStore';
import { usePanelStore } from '../../store/panelStore';
import { Panel } from '../panels/Panel';
import { FolderTree } from './FolderTree';
import { ProjectToolbar } from './ProjectToolbar';
import { AssetGrid } from './AssetGrid';
import { AssetList } from './AssetList';
import { AssetPreview } from './AssetPreview';
import { ProjectContextMenu } from './ProjectContextMenu';

export const ProjectPanel: React.FC = () => {
  const { viewMode, hideContextMenu, deleteSelectedItems, selectAsset, selectedAssetIds, cutItems, copyItems, pasteItems, selectedFolderId } = useProjectPanelStore();
  const panel = usePanelStore((state) => state.panels['project']);
  const panelRef = useRef<HTMLDivElement>(null);
  const filteredAssets = useFilteredAssets();
  const [folderWidth, setFolderWidth] = useState(176); // default w-44 = 176px
  const isResizing = useRef(false);

  // Folder panel resize via drag
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    const startX = e.clientX;
    const startWidth = folderWidth;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = Math.max(120, Math.min(400, startWidth + (ev.clientX - startX)));
      setFolderWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [folderWidth]);

  useEffect(() => {
    const handleScroll = () => hideContextMenu();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [hideContextMenu]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Don't handle if typing in an input/textarea
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;

    if (e.key === 'Delete') {
      e.preventDefault();
      deleteSelectedItems();
    } else if (e.key === 'x' && (e.ctrlKey || e.metaKey)) {
      // Ctrl+X — cut selected assets
      e.preventDefault();
      e.stopPropagation();
      if (selectedAssetIds.length > 0) {
        cutItems(selectedAssetIds, selectedFolderId || 'root');
      }
    } else if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
      // Ctrl+C — copy selected assets
      e.preventDefault();
      e.stopPropagation();
      if (selectedAssetIds.length > 0) {
        copyItems(selectedAssetIds, selectedFolderId || 'root');
      }
    } else if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      // Ctrl+V — paste
      e.preventDefault();
      e.stopPropagation();
      pasteItems(selectedFolderId || 'root');
    } else if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
      // Ctrl+A — select all assets in current folder
      e.preventDefault();
      e.stopPropagation();
      filteredAssets.forEach((asset, i) => {
        selectAsset(asset.id, i > 0);
      });
    }
  }, [deleteSelectedItems, selectAsset, filteredAssets, selectedAssetIds, cutItems, copyItems, pasteItems, selectedFolderId]);

  if (!panel.visible) {
    return null;
  }

  return (
    <Panel panel={panel} className="flex flex-col h-full">
      {/* tabIndex makes the div focusable so it receives keyboard events */}
      <div
        ref={panelRef}
        className="flex-1 flex overflow-hidden outline-none select-none"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="flex-shrink-0 overflow-hidden" style={{ width: folderWidth }}>
          <FolderTree />
        </div>
        {/* Resize handle */}
        <div
          className="w-px flex-shrink-0 cursor-col-resize bg-zinc-700 hover:bg-blue-500 active:bg-blue-500 transition-colors"
          onMouseDown={handleResizeStart}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          <ProjectToolbar />
          <div className="flex-1 flex overflow-hidden relative">
            {viewMode === 'grid' ? <AssetGrid /> : <AssetList />}
            <AssetPreview />
          </div>
        </div>
      </div>
      <ProjectContextMenu />
    </Panel>
  );
};
